import { useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign, TSelectableAutosendSubtype } from '@feathr/blackbox';
import { getAutosendTriggerKind } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';

import { copyRoundedTimeOfDay, getDefaultStartTimestamp } from '../dates.utils';

/**
 * Return value of {@link useMailrailTriggerType}.
 */
export interface IUseMailrailTriggerTypeResult {
  /** The trigger-type radio options (activity / update / time) for the campaign subtype control. */
  triggerTypeOptions: Array<IRadioOption<TSelectableAutosendSubtype>>;
  /** Applies a subtype change, keeping matching predicates and (for `'time'`) seeding dates. */
  handleSubtypeChange: (newValue?: string) => void;
}

/**
 * Shared glue for the Mailrail trigger-type (subtype) control. Provides the radio options and the
 * subtype-change handler that keeps only the predicates of the kind the new subtype evaluates and,
 * for the `'time'` subtype, seeds the start/end dates and disables the bulk sends that can still
 * change.
 *
 * @param campaign - The Mailrail auto-send (or drip) campaign to read defaults from and patch.
 * @returns The trigger-type radio options and the subtype-change handler.
 */
export function useMailrailTriggerType(
  campaign: MailrailAutoSendEmailCampaign,
): IUseMailrailTriggerTypeResult {
  const { t } = useTranslation();
  const defaults = campaign.getDefaults();
  // A started campaign's end bulk send can't be re-added if removed, and date triggers can't have one
  const hasStartedWithEndBulkSend = campaign.hasStarted && campaign.get('bulk_send_at_end');

  const triggerTypeOptions: Array<IRadioOption<TSelectableAutosendSubtype>> = [
    {
      id: 'activity',
      name: t('Activity happened'),
    },
    {
      id: 'update',
      name: t('Field changed'),
    },
    {
      id: 'time',
      name: t('Date triggered'),
      ...(hasStartedWithEndBulkSend && campaign.isEditable
        ? {
            disabled: true,
            tooltip: t('Remove the end-of-campaign bulk send to use date triggers.'),
          }
        : {}),
    },
  ];

  function handleSubtypeChange(newValue?: string): void {
    if (!newValue) {
      return;
    }

    const newSubtype = newValue as TSelectableAutosendSubtype;
    const kind = getAutosendTriggerKind(newSubtype);
    const predicates = campaign
      .get('predicates', [])
      .filter((predicate) => predicate.kind === kind);

    if (newSubtype === 'time') {
      // Time subtype doesn't support the "send now" option, so a start-date is required
      let newStart = campaign.get('date_start');
      if (!newStart) {
        newStart = getDefaultStartTimestamp();
      }
      // Time subtype start and end dates must have the same time component unless campaign has started
      let newEnd = campaign.get('date_end');
      if (!newEnd) {
        const newEndMoment = moment.utc(newStart).add(1, 'day');
        newEnd = newEndMoment.format(TimeFormat.isoDateTime);
      } else if (campaign.isDraft || campaign.isScheduled) {
        newEnd = copyRoundedTimeOfDay(moment.utc(newEnd), moment.utc(newStart)).format(
          TimeFormat.isoDateTime,
        );
      }
      // Even though the backend will ignore delay_seconds and cooldown_seconds when subtype is time,
      // reset them to zero so the data is more representative of the UI state.
      campaign.set({
        date_end: newEnd,
        date_start: newStart,
        bulk_send_at_end: false,
        // A started campaign's start bulk send has already run and can no longer change
        ...(campaign.hasStarted ? {} : { bulk_send_at_start: false }),
        delay_seconds: 0,
        predicates,
        predicates_mode: 'match_any',
        repeat_allowed: false,
        repeat_cooldown_seconds: 0,
        subtype: newSubtype,
      });
    } else if (predicates.length) {
      // The kept triggers keep the delay and mode they were configured with.
      campaign.set({ predicates, subtype: newSubtype });
    } else {
      campaign.set({
        delay_seconds: defaults.delay_seconds,
        predicates,
        predicates_mode: defaults.predicates_mode,
        subtype: newSubtype,
      });
    }
  }

  return { triggerTypeOptions, handleSubtypeChange };
}
